import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { PageHero } from '../components/ui/PageHero';
import { ContactForm } from '../components/forms/ContactForm';
import { Reveal } from '../components/motion/Reveal';
import { partnerInquirySubject, teamMembers } from '../data/team';
import { discussSearchHref } from '../data/navigation';
const routes = [{
  label: 'Discuss a Search',
  text: 'Use the hiring inquiry form',
  to: discussSearchHref
}, {
  label: 'For Candidates',
  text: 'Register your interest in confidence',
  to: '/candidates#candidate-registration'
}, {
  label: 'Meet Our Team',
  text: 'Our Search Executive Partners',
  to: '/our-team'
}];
export function Contact() {
  usePageMeta('Contact | Avenqor Talent Search', 'Contact Avenqor Talent Search to discuss an executive, board or specialist search, or to make a general enquiry.');
  const [params] = useSearchParams();
  const partnerParam = params.get('partner');
  const partner = teamMembers.find(m => m.id === partnerParam || m.name === partnerParam);
  const initialMessage = partner ? `Dear ${partner.name},\n\n` : '';
  return <>
      <PageHero eyebrow="Contact" title={partner ? `Contact ${partner.name}` : 'Begin a confidential conversation.'} intro={partner ? `Prepare a confidential message for ${partner.name}, ${partner.title}.` : 'Whether you are planning an appointment, considering a board search or exploring your own next step, we would be glad to hear from you.'} data-id="element-439" data-mp="1e8inaljvhg97" />

      <section className="bg-paper" aria-labelledby="contact-form-title" data-id="element-440" data-mp="10gc9350a5jce">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:py-28" data-id="element-441" data-mp="10hne185gox53">
          <Reveal className="border border-line bg-white p-6 sm:p-10 lg:col-span-7" data-id="element-442" data-mp="26yiooaf60b7b">
            <h2 id="contact-form-title" className="font-serif text-[2rem] font-medium leading-tight text-navy" data-id="element-443" data-mp="2bto7h0d9t9r6">
              {partner ? 'Message a Search Executive Partner' : 'Send us a message'}
            </h2>
            <p className="mt-2 text-[15px] leading-relaxed text-muted" data-id="element-444" data-mp="1hamwstulmpw2">
              Please avoid including confidential or sensitive details in your first message.
            </p>
            {partner && <dl className="mt-6 divide-y divide-line border border-line bg-paper text-sm" data-id="element-445" data-mp="13gvmvkcyh3zt">
                <div className="flex flex-wrap gap-x-3 gap-y-1 px-4 py-3" data-id="element-446" data-mp="13ej4x7776tmr">
                  <dt className="w-16 shrink-0 text-muted" data-id="element-447" data-mp="yjlz0957t0e3">To</dt>
                  <dd className="text-ink" data-id="element-448" data-mp="1d7ql7n3h7u1m">
                    <span className="font-semibold text-navy" data-id="element-449" data-mp="1d8rxtw054olg">{partner.name}</span>, {partner.title}
                  </dd>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 px-4 py-3" data-id="element-450" data-mp="kpkyb9upd3b3">
                  <dt className="w-16 shrink-0 text-muted" data-id="element-451" data-mp="frb534sjgcs4">Subject</dt>
                  <dd className="text-ink" data-id="element-452" data-mp="fp70ekmtf9qs">{partnerInquirySubject}</dd>
                </div>
              </dl>}
            <div className="mt-8" data-id="element-453" data-mp="2w5d4fh9wi7np">
              <ContactForm key={partner?.id ?? 'general'} initialMessage={initialMessage} partnerId={partner?.id} subject={partner ? partnerInquirySubject : ''} initialEnquiryType={partner ? 'Discussing a search' : ''} data-id="element-454" data-mp="2w6eqshwdagkq" />
            </div>
          </Reveal>

          <aside className="lg:col-span-4 lg:col-start-9" aria-labelledby="routes-title" data-id="element-455" data-mp="3a3bg2ewu7pzi">
            <Reveal data-id="element-456" data-mp="3f24ss54avcym">
              <h2 id="routes-title" className="font-serif text-[1.65rem] font-medium text-navy" data-id="element-457" data-mp="314g6csul3xrg">
                Looking for something specific?
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-458" data-mp="3548a8s919fqh">
                Our dedicated forms help us route your enquiry to the right Search Executive Partner.
              </p>
            </Reveal>
            <ul className="mt-6 space-y-3" data-id="element-459" data-mp="1tos0xuzq7q1x">
              {routes.map((route, index) => <Reveal as="li" key={route.to} delay={index * 60} data-id="element-460" data-mp="29zc24czeixjy" data-mpi={index}>
                  <Link to={route.to} className="group flex items-center justify-between gap-4 border border-line bg-white px-5 py-4 transition-[border-color] duration-200 ease-out hover:border-gold/70 focus-visible:border-gold" data-id="element-461" data-mp="2569d8i5kkca3">
                    <span data-id="element-462" data-mp="2sx4zkzr9af9y">
                      <span className="block text-[15px] font-medium text-navy" data-id="element-463" data-mp="2swd7w00s51oy">{route.label}</span>
                      <span className="mt-0.5 block text-sm text-muted" data-id="element-464" data-mp="2nybqrkmwzcg4">{route.text}</span>
                    </span>
                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-gold-deep transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-465" data-mp="2j0s9scyu0lkh" />
                  </Link>
                </Reveal>)}
            </ul>
          </aside>
        </div>
      </section>
    </>;
}